import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Sparkles, Copy, ArrowRight, Loader2, History as HistoryIcon, RotateCw, GitCompareArrows } from "lucide-react";
import { toast } from "sonner";
import { MarkdownPreview } from "@/components/MarkdownEditor";
import { DiffView } from "@/components/DiffView";

const TASKS = [
  { key: "policy_summary", label: "Policy summary" },
  { key: "decision_log", label: "Decision log entry" },
  { key: "meeting_minutes", label: "Meeting minutes" },
  { key: "rule_draft", label: "House rule draft" },
  { key: "lessons_learned", label: "Lessons learned" },
  { key: "generic", label: "Freeform" },
];

const TASK_LABEL = TASKS.reduce((a, t) => { a[t.key] = t.label; return a; }, {});

/**
 * WikiAiDialog — Claude-powered draft assistant that returns Markdown.
 * "Compose" tab drafts a new markdown block; "History" tab lists every past
 * Claude draft this director generated with one-click Restore.
 */
export function WikiAiDialog({ open, onOpenChange, currentBody, currentTitle, currentTags, onAccept }) {
  const [tab, setTab] = useState("compose");
  const [task, setTask] = useState("policy_summary");
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState("");
  const [showDiff, setShowDiff] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/board/wiki/ai/history");
      setHistory(data.items || []);
      setHistoryLoaded(true);
    } catch (e) { toast.error(apiErrorMessage(e) || "Failed to load history"); }
  }, []);

  useEffect(() => {
    if (open && tab === "history" && !historyLoaded) {
      loadHistory();
    }
  }, [open, tab, historyLoaded, loadHistory]);

  useEffect(() => {
    if (!open) {
      // Reset transient state on close so next open feels fresh
      setDraft("");
      setPrompt("");
      setTab("compose");
      setHistoryLoaded(false);
      setShowDiff(false);
    }
  }, [open]);

  const gen = async () => {
    if (!prompt.trim()) return toast.error("Add a prompt first");
    setBusy(true);
    setDraft("");
    try {
      const { data } = await api.post("/board/wiki/ai/draft", {
        prompt: prompt.trim(),
        task,
        existing_body: currentBody || undefined,
        title: currentTitle || undefined,
        tags: currentTags || undefined,
      });
      setDraft(data.markdown || "");
      setHistoryLoaded(false);  // Force refresh next time History tab is opened
    } catch (e) { toast.error(apiErrorMessage(e) || "AI draft failed"); }
    setBusy(false);
  };

  const restore = (h) => {
    setDraft(h.markdown || "");
    setTask(h.task || "generic");
    setPrompt(h.prompt || "");
    setTab("compose");
    toast.success("Restored draft — click Append or Replace to apply.");
  };

  const applyReplace = () => {
    if (!draft) return;
    onAccept?.(draft);
    onOpenChange(false);
  };

  const applyAppend = () => {
    if (!draft) return;
    const glue = currentBody?.trim() ? "\n\n---\n\n" : "";
    onAccept?.((currentBody || "") + glue + draft);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0b] border-white/10 text-white sm:max-w-[860px] max-h-[90vh] overflow-hidden flex flex-col" data-testid="wiki-ai-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-orange-400" /> AI Draft
          </DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Ask Claude Sonnet 4.5 to draft policy summaries, decision logs, or house rules. Every draft is archived — flip to History to restore any past version.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <TabsList className="bg-white/[0.04] border border-white/10 mx-1 mb-2 shrink-0" data-testid="ai-tabs">
            <TabsTrigger value="compose" data-testid="ai-tab-compose">
              <Sparkles className="h-3 w-3 mr-1" /> Compose
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="ai-tab-history">
              <HistoryIcon className="h-3 w-3 mr-1" /> History{history.length ? ` (${history.length})` : ""}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="compose" className="flex-1 min-h-0 mt-0 overflow-hidden">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 h-full min-h-0 overflow-hidden">
              <div className="space-y-2 min-w-0 overflow-y-auto pr-1">
                <div>
                  <Label className="text-xs text-zinc-400">Task</Label>
                  <Select value={task} onValueChange={setTask}>
                    <SelectTrigger className="bg-white/[0.04] border-white/10" data-testid="ai-task">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
                      {TASKS.map((t) => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Prompt</Label>
                  <Textarea
                    rows={12}
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder="Summarise the Q1 board vote on pool hours: extended to 10pm from June-August, minimum 3 director sign-off, revisit in Sept…"
                    className="bg-white/[0.04] border-white/10 font-mono text-xs"
                    data-testid="ai-prompt"
                  />
                </div>
                {currentBody?.trim() && (
                  <div className="text-[11px] text-zinc-500 rounded-md border border-white/10 bg-white/[0.02] p-2" data-testid="ai-context-badge">
                    Existing page body will be sent as context (integrate or improve on it).
                  </div>
                )}
                <Button onClick={gen} disabled={busy || !prompt.trim()}
                        className="w-full bg-orange-500 hover:bg-orange-600"
                        data-testid="ai-generate">
                  {busy ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Drafting…</>
                        : <><Sparkles className="h-3 w-3 mr-1" /> Generate draft</>}
                </Button>
              </div>

              <div className="rounded-md border border-white/10 bg-white/[0.02] p-3 overflow-y-auto min-h-[300px]" data-testid="ai-preview">
                {draft ? (
                  <>
                    <div className="flex items-center justify-between mb-2 gap-2">
                      <Badge variant="outline" className="border-emerald-500/40 text-emerald-300">
                        {showDiff ? "Diff · current vs after" : "Draft ready"}
                      </Badge>
                      <div className="flex items-center gap-1">
                        {currentBody?.trim() && (
                          <button
                            onClick={() => setShowDiff((v) => !v)}
                            className={"text-[10px] uppercase tracking-widest px-2 py-1 rounded border transition " +
                              (showDiff
                                ? "border-orange-500/40 bg-orange-500/10 text-orange-200"
                                : "border-white/10 text-zinc-400 hover:text-white")}
                            data-testid="ai-diff-toggle"
                            title="Toggle diff view against current body"
                          >
                            <GitCompareArrows className="h-3 w-3 inline mr-1" />
                            {showDiff ? "Hide diff" : "Show diff"}
                          </button>
                        )}
                        <button
                          onClick={() => { navigator.clipboard.writeText(draft); toast.success("Copied"); }}
                          className="text-zinc-500 hover:text-white p-1"
                          data-testid="ai-copy"
                          title="Copy markdown"
                        >
                          <Copy className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                    {showDiff ? (
                      <DiffView before={currentBody || ""} after={draft} />
                    ) : (
                      <MarkdownPreview value={draft} />
                    )}
                  </>
                ) : busy ? (
                  <div className="flex items-center justify-center h-full text-zinc-500 text-xs">
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Consulting the wiki assistant…
                  </div>
                ) : (
                  <div className="text-xs text-zinc-500 italic">
                    Draft preview appears here. Pick a task, describe what you need, and hit Generate.
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="history" className="flex-1 min-h-0 mt-0 overflow-hidden">
            <div className="rounded-md border border-white/10 bg-white/[0.02] p-3 h-full overflow-y-auto space-y-2" data-testid="ai-history-list">
              {!historyLoaded ? (
                <div className="text-xs text-zinc-500 italic flex items-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" /> Loading past drafts…
                </div>
              ) : history.length === 0 ? (
                <div className="text-xs text-zinc-500 italic" data-testid="ai-history-empty">
                  No past drafts yet. Once you generate one, it&apos;ll show up here with a one-click Restore.
                </div>
              ) : history.map((h) => (
                <div key={h.id} className="rounded-md border border-white/10 bg-white/[0.02] p-3"
                     data-testid={`ai-history-item-${h.id}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <Badge variant="outline" className="border-orange-500/40 text-orange-300 capitalize">
                      {TASK_LABEL[h.task] || h.task}
                    </Badge>
                    {h.title && <span className="text-xs text-zinc-300 truncate max-w-[280px]">{h.title}</span>}
                    <span className="text-[10px] text-zinc-500 ml-auto">
                      {new Date(h.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-[11px] text-zinc-500 line-clamp-2 mb-2">{h.prompt}</div>
                  <div className="flex items-center justify-end gap-2">
                    <Button size="sm" variant="outline"
                            onClick={() => { navigator.clipboard.writeText(h.markdown || ""); toast.success("Copied"); }}
                            className="border-white/10 h-7 px-2"
                            data-testid={`ai-history-copy-${h.id}`}>
                      <Copy className="h-3 w-3 mr-1" /> Copy
                    </Button>
                    <Button size="sm" onClick={() => restore(h)}
                            className="bg-orange-500 hover:bg-orange-600 h-7 px-2"
                            data-testid={`ai-history-restore-${h.id}`}>
                      <RotateCw className="h-3 w-3 mr-1" /> Restore
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-white/10">Cancel</Button>
          <Button variant="outline" onClick={applyAppend} disabled={!draft} className="border-white/10" data-testid="ai-append">
            Append to page <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
          <Button onClick={applyReplace} disabled={!draft} className="bg-orange-500 hover:bg-orange-600" data-testid="ai-replace">
            Replace body <ArrowRight className="h-3 w-3 ml-1" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default WikiAiDialog;
